// src/lib/firebase.ts
// Keep this file imported **only** from client components.
// Do NOT import from server files or app/page directly.

import { initializeApp, getApps, getApp, FirebaseApp } from "firebase/app";
import {
  getFirestore,
  connectFirestoreEmulator,
  Firestore,
} from "firebase/firestore";

let app: FirebaseApp | undefined;
let db: Firestore | undefined;

function getFirebaseConfig() {
  // Values are inlined at build-time for NEXT_PUBLIC_* envs.
  const config = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    measurementId: process.env.NEXT_PUBLIC_MEASUREMENT_ID,
  } as const;

  // Validate – throw a readable error early rather than crashing mysteriously.
  const missing = Object.entries(config)
    .filter(([, v]) => !v)
    .map(([k]) => k);

  if (missing.length) {
    const msg =
      `Missing Firebase config env(s): ${missing.join(", ")}. ` +
      `Make sure NEXT_PUBLIC_* vars are defined at build time.`;
    // Helpful both in dev and in production console
    // eslint-disable-next-line no-console
    console.error(msg);
    throw new Error(msg);
  }

  return config as Required<typeof config>;
}

export function getFirebaseApp(): FirebaseApp {
  // Guard: only run on the client
  if (typeof window === "undefined") {
    throw new Error(
      "getFirebaseApp() called on the server. Import it only in client components."
    );
  }

  if (app) return app;

  const cfg = getFirebaseConfig();
  app = getApps()[0] ?? initializeApp(cfg);
  return app;
}

export function getFirebaseDb(): Firestore {
  if (db) return db;

  const firebaseApp = getFirebaseApp();
  db = getFirestore(firebaseApp);

  // Optional emulator only when running locally
  if (
    location.hostname === "localhost" &&
    process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATORS === "true"
  ) {
    try {
      connectFirestoreEmulator(db, "127.0.0.1", 8080);
    } catch {
      // ignore if already connected
    }
  }

  return db;
}
